'use client';

import {
  SortCriterion,
  loadAdvancedSearchQueryActions,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  loadSortCriteriaActions,
} from '@coveo/atomic-react';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { Maybe } from '@lib/types/Maybe';
import { ReactNode, useEffect, useRef } from 'react';
import { AtomicListingContext } from './AtomicListingContext';
import { useCoveoHeadlessEngine } from './useCoveoHeadlessEngine';

type HeadlessListingProviderProps = {
  uniqueId: string;
  queryExpression: string;
  numberOfResults: number;
  sortCriteria: {
    name: Maybe<string>;
    criterion: SortCriterion | SortCriterion[];
  };
  searchQuery?: string;
  children: ReactNode;
};

/**
 * Headless drop-in replacement for AtomicListingProvider + AtomicSearchInterfaceWrapper.
 * Creates a scoped engine per instance — no URL state, no Atomic web components.
 * Provides the engine via AtomicListingContext so existing consumers work unchanged.
 * Engine is scoped to the component lifecycle and cleaned up on unmount.
 */
export const HeadlessListingProvider = ({
  uniqueId,
  queryExpression,
  numberOfResults,
  sortCriteria,
  searchQuery,
  children,
}: HeadlessListingProviderProps) => {
  const value = useCoveoHeadlessEngine(
    CoveoSearchHub.LISTING,
    `${CoveoSearchHub.LISTING}_${uniqueId}`,
    numberOfResults
  );
  const firstSearchExecuted = useRef(false);

  const { searchEngine, coveoContext } = value;

  useEffect(() => {
    if (!searchEngine || !coveoContext) return;

    searchEngine.dispatch(
      loadAdvancedSearchQueryActions(searchEngine).updateAdvancedSearchQueries({
        aq: queryExpression,
      })
    );

    searchEngine.dispatch(
      loadSortCriteriaActions(searchEngine).registerSortCriterion(
        sortCriteria.criterion
      )
    );

    coveoContext.add(
      'popularity',
      String(sortCriteria.name === 'Popularity') as 'true' | 'false'
    );

    if (searchQuery) {
      searchEngine.dispatch(
        loadQueryActions(searchEngine).updateQuery({ q: searchQuery })
      );
    }

    if (!firstSearchExecuted.current) {
      firstSearchExecuted.current = true;
      searchEngine.dispatch(
        loadSearchActions(searchEngine).executeSearch(
          loadSearchAnalyticsActions(searchEngine).logInterfaceLoad()
        )
      );
    } else {
      // logSearchboxSubmit distinguishes subsequent triggered searches from the initial interface load in Coveo analytics
      searchEngine.dispatch(
        loadSearchActions(searchEngine).executeSearch(
          loadSearchAnalyticsActions(searchEngine).logSearchboxSubmit()
        )
      );
    }
  }, [searchEngine, coveoContext, queryExpression, sortCriteria, searchQuery]);

  if (!searchEngine || !coveoContext) {
    return null;
  }

  return (
    <AtomicListingContext.Provider value={value}>
      {children}
    </AtomicListingContext.Provider>
  );
};
